import { PageBreadcrumb } from "@modules/shared/Breadcrumbs/PageBreadcrumb";
import { ProductGrid } from "@modules/shop/components/ProductCard";
import {
  CATEGORY_GROUPS,
  PRODUCTS_DEFAULT_SEARCH,
} from "@modules/shop/components/Products/filter-params";
import { ProductDetails } from "@modules/shop/components/SingleProduct/ProductDetails";
import { ProductImageGallery } from "@modules/shop/components/SingleProduct/ProductImageGallery";
import { ProductOptions } from "@modules/shop/components/SingleProduct/ProductOptions";
import { QuantitySelector } from "@modules/shop/components/SingleProduct/QuantitySelector";
import { SizeGuideDialog } from "@modules/shop/components/SingleProduct/SizeGuideDialog";
import { LegalGuaranteeNotice } from "@modules/shop/components/LegalGuarantee/LegalGuaranteeNotice";
import { BasePrice } from "@modules/shared/Product/BasePrice";
import {
  useGetProductByHandleQuery,
  useGetProductRecommendationsQuery,
} from "@modules/shopify/hooks";
import { dispatchAddToCartAnalytics } from "@modules/shopify/analytics/shopifyAnalytics";
import { useViewAnalytics } from "@modules/shopify/analytics/useViewAnalytics";
import { Checkbox } from "@ui/components/ui/checkbox";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@ui/components/ui/field";
import {
  AnalyticsEventName,
  Money,
  ProductProvider,
  useCart,
} from "@shopify/hydrogen-react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MoneyV2 } from "@shopify/hydrogen-react/storefront-api-types";
import type {
  GetProductByHandleQuery_QueryRoot_product_Product,
  GetProductByIdQuery_QueryRoot_product_Product,
} from "@/gql/types";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";

interface SingleProductPageViewProps {
  handle: string;
  locale: string;
  heelInsoleAddonProduct?: GetProductByIdQuery_QueryRoot_product_Product | null;
}

type ProductOption =
  GetProductByHandleQuery_QueryRoot_product_Product["options"][number];
type ProductVariant =
  GetProductByHandleQuery_QueryRoot_product_Product["variants"]["nodes"][number];

// Shopify taxonomy "Shoes" (aa-8) and its children: slippers (aa-8-7), sandals (aa-8-6).
// Product types can't be used: CATEGORY_GROUPS.footwear misses several shoe types.
const SHOES_CATEGORY = /^gid:\/\/shopify\/TaxonomyCategory\/aa-8(-|$)/;

function hasOptionWithPriceIncrease(
  options: Array<ProductOption>,
  variants: Array<ProductVariant>,
  expectedIncrease: number,
) {
  if (!Number.isFinite(expectedIncrease) || expectedIncrease <= 0) {
    return false;
  }

  return options.some((option) =>
    option.values.some((value) =>
      variants.some((variant) => {
        const variantUsesOptionValue = variant.selectedOptions.some(
          (selectedOption) =>
            selectedOption.name === option.name &&
            selectedOption.value === value,
        );

        if (!variantUsesOptionValue) {
          return false;
        }

        const matchingOptions = variant.selectedOptions.filter(
          (selectedOption) => selectedOption.name !== option.name,
        );
        const comparablePrices = variants
          .filter((candidate) =>
            matchingOptions.every((selectedOption) =>
              candidate.selectedOptions.some(
                (candidateOption) =>
                  candidateOption.name === selectedOption.name &&
                  candidateOption.value === selectedOption.value,
              ),
            ),
          )
          .map((candidate) => Number(candidate.price.amount))
          .filter(Number.isFinite);
        const basePrice = Math.min(...comparablePrices);
        const priceIncrease = Number(variant.price.amount) - basePrice;

        return (
          Number.isFinite(basePrice) &&
          Math.abs(priceIncrease - expectedIncrease) < 0.01
        );
      }),
    ),
  );
}

export function SingleProductPageView({
  handle,
  locale,
  heelInsoleAddonProduct,
}: Readonly<SingleProductPageViewProps>) {
  const { t } = useTranslation();
  const localeVars = getCountryVariables(locale);

  const { data } = useGetProductByHandleQuery({ handle, ...localeVars });

  const product = data?.product;

  if (!product) {
    return <ProductNotFound locale={locale} />;
  }

  return (
    <ProductProvider data={product}>
      <div className="min-h-screen px-4 pt-8 pb-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <PageBreadcrumb
            className="mb-8"
            segments={[
              {
                label: t("SingleProductPage.breadcrumbs.home"),
                to: "/$locale",
                params: { locale },
              },
              {
                label: t("SingleProductPage.breadcrumbs.products"),
                to: "/$locale/products",
                params: { locale },
              },
              { label: product.title },
            ]}
          />

          <ProductContent
            product={product}
            heelInsoleAddonProduct={heelInsoleAddonProduct ?? null}
          />

          <RecommendedProducts
            productId={product.id}
            locale={locale}
            localeVars={localeVars}
          />
        </div>
      </div>
    </ProductProvider>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function ProductNotFound({ locale }: Readonly<{ locale: string }>) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-xlarge text-text-1 mb-4 font-bold">
          {t("SingleProductPage.notFound")}
        </h1>
        <Link
          to="/$locale/products"
          params={{ locale }}
          search={PRODUCTS_DEFAULT_SEARCH}
          className="text-primary hover:text-primary-darker1"
        >
          {t("SingleProductPage.backToProducts")}
        </Link>
      </div>
    </div>
  );
}

function ProductContent({
  product,
  heelInsoleAddonProduct,
}: Readonly<{
  product: GetProductByHandleQuery_QueryRoot_product_Product;
  heelInsoleAddonProduct?: GetProductByIdQuery_QueryRoot_product_Product | null;
}>) {
  const { t, i18n } = useTranslation();
  const { linesAdd } = useCart();

  const images = product.images.nodes;
  const variants = product.variants.nodes;
  const options = product.options;

  const [selectedVariant, setSelectedVariant] = useState(variants[0]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isHeelInsoleAddonSelected, setIsHeelInsoleAddonSelected] =
    useState(false);

  const isFootwear = (
    CATEGORY_GROUPS.footwear as ReadonlyArray<string>
  ).includes(product.productType);
  const heelInsoleAddonVariant = heelInsoleAddonProduct?.variants.nodes[0];
  const heelInsoleAddonPrice = Number(heelInsoleAddonVariant?.price.amount);
  const hasHeelInsoleVariantOption = hasOptionWithPriceIncrease(
    options,
    variants,
    heelInsoleAddonPrice,
  );
  const canRenderHeelInsoleAddon = Boolean(
    isFootwear &&
    !hasHeelInsoleVariantOption &&
    heelInsoleAddonProduct &&
    heelInsoleAddonVariant?.id,
  );
  const isHeelInsoleAddonAvailable = Boolean(
    heelInsoleAddonProduct?.availableForSale &&
    heelInsoleAddonVariant?.availableForSale,
  );

  useViewAnalytics(
    {
      eventName: AnalyticsEventName.PRODUCT_VIEW,
      products: [
        {
          productGid: product.id,
          variantGid: selectedVariant.id,
          name: product.title,
          variantName: selectedVariant.title,
          brand: product.vendor,
          category: product.productType,
          price: String(selectedVariant.price.amount),
          sku: selectedVariant.sku ?? undefined,
          quantity: 1,
        },
      ],
      totalValue: Number(selectedVariant.price.amount),
    },
    product.id,
  );

  const handleAddToCart = () => {
    if (!selectedVariant.id) return;

    const lines = [{ merchandiseId: selectedVariant.id, quantity }];
    const analyticsProducts = [
      {
        productGid: product.id,
        variantGid: selectedVariant.id,
        name: product.title,
        variantName: selectedVariant.title,
        brand: product.vendor,
        category: product.productType,
        price: selectedVariant.price.amount,
        sku: selectedVariant.sku,
        quantity,
      },
    ];

    if (
      isFootwear &&
      isHeelInsoleAddonSelected &&
      heelInsoleAddonVariant?.id &&
      isHeelInsoleAddonAvailable
    ) {
      lines.push({ merchandiseId: heelInsoleAddonVariant.id, quantity });

      if (heelInsoleAddonProduct) {
        analyticsProducts.push({
          productGid: heelInsoleAddonProduct.id,
          variantGid: heelInsoleAddonVariant.id,
          name: heelInsoleAddonProduct.title,
          variantName: heelInsoleAddonVariant.title,
          brand: heelInsoleAddonProduct.vendor,
          category: heelInsoleAddonProduct.productType,
          price: heelInsoleAddonVariant.price.amount,
          sku: heelInsoleAddonVariant.sku,
          quantity,
        });
      }
    }

    linesAdd(lines);
    dispatchAddToCartAnalytics({
      products: analyticsProducts,
      totalValue: analyticsProducts.reduce(
        (sum, item) => sum + Number(item.price) * (item.quantity ?? 1),
        0,
      ),
    });
  };

  return (
    <div className="grid items-start gap-12 lg:grid-cols-[1.1fr_1fr] max-mobile:gap-8">
      <ProductImageGallery
        images={images}
        productTitle={product.title}
        productHandle={product.handle}
        selectedIndex={selectedImage}
        onSelect={setSelectedImage}
      />

      <div className="lg:sticky lg:top-6">
        {product.productType && (
          <p className="mb-2 text-small text-text-4">{product.productType}</p>
        )}
        <h1
          className="mb-4 text-3xlarge leading-tight font-bold tracking-[-0.02em] text-balance text-text-1 max-mobile:text-xlarge"
          style={{ viewTransitionName: `product-title-${product.handle}` }}
        >
          {product.title}
        </h1>

        {/* Price */}
        <div
          className="mb-2 flex items-baseline gap-3 tabular-nums"
          style={{ viewTransitionName: `product-price-${product.handle}` }}
        >
          <Money
            data={selectedVariant.price as MoneyV2}
            className="text-xlarge font-semibold text-text-1 max-mobile:text-large"
          />
          {selectedVariant.compareAtPrice && (
            <Money
              data={selectedVariant.compareAtPrice as MoneyV2}
              className="text-medium text-text-4 line-through"
            />
          )}
        </div>
        <BasePrice
          metafield={selectedVariant.basePrice}
          className="mb-2 text-small text-text-3"
        />
        <p className="mb-2 text-small text-text-3">
          {t("SingleProductPage.priceNote")}
        </p>
        <div className="mb-8 border-b border-border-5 pb-8">
          <LegalGuaranteeNotice locale={i18n.language} />
        </div>

        <ProductOptions
          options={options}
          variants={variants}
          selectedVariant={selectedVariant}
          onSelectVariant={setSelectedVariant}
        />

        {canRenderHeelInsoleAddon && heelInsoleAddonVariant && (
          <FieldLabel
            htmlFor="heel-insole-addon"
            className="mb-6 w-full cursor-pointer rounded-2xl bg-white ring-1 ring-black/[0.08] transition-shadow has-data-[state=checked]:bg-primary-lighter3/40 has-data-[state=checked]:ring-2 has-data-[state=checked]:ring-primary"
          >
            <Field
              orientation="horizontal"
              data-disabled={!isHeelInsoleAddonAvailable || undefined}
            >
              <Checkbox
                id="heel-insole-addon"
                checked={isHeelInsoleAddonSelected}
                disabled={!isHeelInsoleAddonAvailable}
                onCheckedChange={(checked) =>
                  setIsHeelInsoleAddonSelected(Boolean(checked))
                }
                aria-label={heelInsoleAddonProduct.title}
                size="normal"
              />

              <FieldContent>
                <FieldTitle className="text-medium text-text-1">
                  {heelInsoleAddonProduct.title}
                </FieldTitle>
                <FieldDescription className="text-primary font-semibold">
                  +
                  <Money
                    data={heelInsoleAddonVariant.price as MoneyV2}
                    className="inline-flex"
                  />
                </FieldDescription>
                <BasePrice metafield={heelInsoleAddonVariant.basePrice} />
              </FieldContent>
            </Field>
          </FieldLabel>
        )}

        {isFootwear && <SizeGuideDialog />}

        <div className="flex gap-3">
          <QuantitySelector value={quantity} onChange={setQuantity} />
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={!selectedVariant.availableForSale}
            className="h-12 flex-1 rounded-full bg-primary px-6 font-semibold text-white transition-[background-color,scale] duration-150 ease-out hover:bg-primary-darker1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100"
          >
            {selectedVariant.availableForSale
              ? t("SingleProductPage.addToCart")
              : t("SingleProductPage.soldOut")}
          </button>
        </div>

        <ProductDetails
          descriptionHtml={product.descriptionHtml}
          vendor={product.vendor}
          productType={product.productType}
          isShoe={SHOES_CATEGORY.test(product.category?.id ?? "")}
          isHeelInsoleAddon={product.id === heelInsoleAddonProduct?.id}
          hasMetalParts={!product.tags.includes("bez-metalnih-dijelova")}
          sku={selectedVariant.sku}
        />
      </div>
    </div>
  );
}

function RecommendedProducts({
  productId,
  locale,
  localeVars,
}: Readonly<{
  productId: string;
  locale: string;
  localeVars: ReturnType<typeof getCountryVariables>;
}>) {
  const { t } = useTranslation();

  const { data: recommendationsData } = useGetProductRecommendationsQuery({
    productId,
    ...localeVars,
  });

  const recommendations = recommendationsData?.productRecommendations ?? [];

  if (recommendations.length === 0) return null;

  return (
    <div className="mt-24 max-mobile:mt-16">
      <h2 className="mb-8 text-xlarge leading-tight font-bold tracking-[-0.01em] text-balance text-text-1 max-mobile:text-large">
        {t("SingleProductPage.relatedProducts")}
      </h2>
      <ProductGrid
        products={recommendations.slice(0, 4)}
        locale={locale}
        columns={4}
      />
    </div>
  );
}
